import React, {
  useEffect,
  useRef,
  useContext,
  useCallback,
  useState,
} from 'react'
import update from 'immutability-helper'
import * as Sentry from '@sentry/browser'
import { getUnixTime } from 'date-fns'

import './PdfRender.scss'
import { PreviewContext } from '../../context/previewContext'
import { LoaderContext } from '../../context/loaderContext'
import { centeringPDFByHeight } from '../../shared/utils'
import { PdfContext } from '../../context/pdfContext'
import EventBus from '../../shared/eventBus'
import NullL10n from '../../shared/nullL10n'
import { ShapeContext } from '../../context/shapeContext'
import { getWindowScaleList } from '../../shared/zoomHelper'

const PdfRender = () => {
  const [padding, setPadding] = useState(
    `${window.innerHeight / 2}px ${window.innerWidth / 2}px`
  )
  const [eventBus, setEventBus] = useState(null)
  const [l10n, setL10n] = useState(null)

  const containerViewRef = useRef()
  const pdfViewerRef = useRef()

  const { isNewVersionCanvas } = useContext(ShapeContext)
  const { onLoadedPercent } = useContext(LoaderContext)
  const {
    url,
    onImageBoundRectChanged,
    isLoadingFile,
    onIsLoadingFile,
    byteLength,
    imageBoundRect,
    updateZoomDetail,
  } = useContext(PreviewContext)

  const {
    pdfViewerState,
    onPdfViewerState,
    pdfViewerWrappRef,
    onChangeScale,
    marginPage,
    updateTotalPage,
  } = useContext(PdfContext)

  const updateTotalPageCallback = useCallback(updateTotalPage, [
    updateTotalPage,
  ])

  useEffect(() => {
    setEventBus(new EventBus())
    setL10n(NullL10n)

    /**
     * PDFJS version 2.5.207, served from public/pdfjs/ rather than a CDN so
     * that the DeviceCMYK colour patch applied at postinstall is in effect.
     * See scripts/patch-pdfjs-cmyk.js.
     */
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdfjs/pdf.worker.js`
  }, [])

  useEffect(() => {
    if (eventBus) {
      let isMounted = true // track whether component is mounted
      const container = containerViewRef.current

      // (Optionally) enable hyperlinks within PDF files.
      const pdfLinkService = new window.pdfjsViewer.PDFLinkService({
        eventBus,
      })

      // (Optionally) enable find controller.
      const pdfFindController = new window.pdfjsViewer.PDFFindController({
        linkService: pdfLinkService,
        eventBus,
      })

      const pdfViewer = new window.pdfjsViewer.PDFViewer({
        container: container,
        linkService: pdfLinkService,
        findController: pdfFindController,
        eventBus,
      })
      pdfLinkService.setViewer(pdfViewer)

      // Loading document.
      const loadingTask = window.pdfjsLib.getDocument({
        url: `${url}?v=${getUnixTime(new Date())}`,
        cMapUrl: 'https://static.a.ngine.com.au/annotation/bcmaps/',
        cMapPacked: true,
      })

      loadingTask.onProgress = (event) => {
        if (isMounted && byteLength > 0) {
          if (event) {
            let totalByte = event.loaded

            if (event.total) {
              totalByte = event.total
            } else if (!event.total && byteLength > 0) {
              totalByte = byteLength
            }

            let loaded = (
              (parseFloat(event.loaded) / parseFloat(totalByte)) *
              100
            ).toFixed(0)

            if (typeof onLoadedPercent === 'function') {
              onLoadedPercent(`${loaded}%`)
            }
          } else {
            if (typeof onLoadedPercent === 'function') {
              onLoadedPercent(`100%`)
            }
          }
        }
      }

      onPdfViewerState(pdfViewer)

      loadingTask.promise.then(
        (pdfDocument) => {
          if (isMounted) {
            pdfViewer.setDocument(pdfDocument)
            pdfLinkService.setDocument(pdfDocument, null)
          }
        },
        (exception) => {
          if (isMounted) {
            let loadingErrorMessage

            if (exception instanceof window.pdfjsLib.InvalidPDFException) {
              loadingErrorMessage = l10n.get(
                'invalid_file_error',
                null,
                'Invalid or corrupted PDF file.'
              )
            } else if (
              exception instanceof window.pdfjsLib.MissingPDFException
            ) {
              loadingErrorMessage = l10n.get(
                'missing_file_error',
                null,
                'Missing PDF file.'
              )
            } else if (
              exception instanceof window.pdfjsLib.UnexpectedResponseException
            ) {
              loadingErrorMessage = l10n.get(
                'unexpected_response_error',
                null,
                'Unexpected server response.'
              )
            } else {
              loadingErrorMessage = l10n.get(
                'loading_error',
                null,
                'An error occurred while loading the PDF.'
              )
            }

            return loadingErrorMessage.then((msg) => {
              alert(msg)

              if (process.env.NODE_ENV === 'production') {
                Sentry.captureException(msg)
              }
              // throw exception;
            })
          }
        }
      )

      return () => {
        isMounted = false

        if (loadingTask.destroy && typeof loadingTask.destroy === 'function') {
          loadingTask.destroy()
        }
      }
    }
  }, [url, onLoadedPercent, onPdfViewerState, byteLength, eventBus, l10n])

  /**
   * @deprecated at @version 0.2
   * this for old annotation canvas
   */
  // update margin each pages
  useEffect(() => {
    if (
      !isNewVersionCanvas &&
      pdfViewerRef.current &&
      pdfViewerRef.current.hasChildNodes()
    ) {
      const pagesEl = pdfViewerRef.current.querySelectorAll('.page')
      pagesEl.forEach((page, idx) => {
        if (idx > 0) {
          page.style.marginTop = `${marginPage}px`
        }
      })
    } else {
      const pagesEl = pdfViewerRef.current.querySelectorAll('.page')
      pagesEl.forEach((page) => {
        page.classList.add('pdf-viewer__page')
        page.style.removeProperty('margin-top')
      })
    }
  }, [isNewVersionCanvas, marginPage])

  const centerPositionPDF = useCallback(() => {
    if (
      pdfViewerWrappRef.current &&
      pdfViewerRef.current &&
      pdfViewerRef.current.querySelector('.page')
    ) {
      const pageEl = pdfViewerRef.current.querySelector('.page')
      const heightPage = pageEl.clientHeight

      centeringPDFByHeight(pdfViewerWrappRef.current, heightPage)
    }
  }, [pdfViewerWrappRef])

  const handlePagesloaded = useCallback(
    (_) => {
      onChangeScale(parseFloat(imageBoundRect.scale))

      setTimeout(() => {
        onIsLoadingFile(false)
        centerPositionPDF()

        setTimeout(() => {
          const evt = new Event('annotationFileReady', {
            bubbles: true,
            cancelable: false,
          })
          document.dispatchEvent(evt)
        }, 250)
      }, 300)
    },
    [onChangeScale, imageBoundRect.scale, onIsLoadingFile, centerPositionPDF]
  )

  const handlePagesinit = useCallback(
    function () {
      if (pdfViewerState) {
        pdfViewerState.currentScaleValue = 1

        updateTotalPageCallback(pdfViewerState.pagesCount)

        /**
         * @deprecated at @version 0.2
         * this for old annotation canvas
         */
        // set margin on init
        if (
          !isNewVersionCanvas &&
          pdfViewerRef.current &&
          pdfViewerRef.current.hasChildNodes()
        ) {
          const pagesEl = pdfViewerRef.current.querySelectorAll('.page')
          pagesEl.forEach((page, idx) => {
            if (idx > 0) {
              page.style.marginTop = `${marginPage}px`
            }
          })
        } else {
          const pagesEl = pdfViewerRef.current.querySelectorAll('.page')
          pagesEl.forEach((page) => {
            page.classList.add('pdf-viewer__page')
            page.style.removeProperty('margin-top')
          })
        }

        pdfViewerState.pdfDocument.getPage(1).then((page) => {
          const viewport = page.getViewport({ scale: 1 })
          const naturalWidth = viewport.width
          const naturalHeight = viewport.height

          const resultScale = getWindowScaleList(naturalWidth, naturalHeight)

          const newImageBoundRect = {
            naturalWidth: { $set: naturalWidth },
            naturalHeight: { $set: naturalHeight },
            width: { $set: naturalWidth * resultScale.initialScale },
            height: { $set: naturalHeight * resultScale.initialScale },
            scale: { $set: resultScale.initialScale },
          }

          onImageBoundRectChanged((v) => update(v, newImageBoundRect))

          updateZoomDetail(resultScale)
        })
      }
    },
    [
      isNewVersionCanvas,
      marginPage,
      onImageBoundRectChanged,
      pdfViewerState,
      updateTotalPageCallback,
      updateZoomDetail,
    ]
  )

  const handleResize = useCallback(() => {
    setPadding(`${window.innerHeight / 2}px ${window.innerWidth / 2}px`)
  }, [])

  useEffect(() => {
    if (eventBus) {
      eventBus.on('pagesloaded', handlePagesloaded)
      eventBus.on('pagesinit', handlePagesinit)
      eventBus.on('resize', handleResize)

      return () => {
        eventBus.off('pagesloaded', handlePagesloaded)
        eventBus.off('pagesinit', handlePagesinit)
        eventBus.off('resize', handleResize)
      }
    }
  }, [eventBus, handlePagesinit, handlePagesloaded, handleResize])

  return (
    <div
      className="viewer-container"
      ref={containerViewRef}
      style={{
        padding: padding,
        visibility: isLoadingFile ? 'hidden' : 'visible',
      }}
    >
      <div id="viewer" className="pdf-viewer" ref={pdfViewerRef}></div>
    </div>
  )
}

export default React.memo(PdfRender)
